import React, { useState, useEffect, useContext } from "react";
import {
    Alert,
    Button,
    TextField,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Snackbar,
    Chip,
    InputLabel
} from "@mui/material";
import {
    MonetizationOn,
    History,
    Warning,
    CheckCircle,
    ArrowBack,
    Person,
    AccountBalance
} from "@mui/icons-material";
import { AdminProjectContext } from "../../contexts/AdminProjectContext";
import { AdminUserContext } from "../../contexts/AdminUserContext";
import { CommonContext } from "../../contexts/CommonContext";
import useCurrentUser from "../../hooks/useCurrentUser";
import useAxios from "../../hooks/useAxios";
import { CashOutRequest, CashOutFormData } from "../../interface/CashOut.interface";
import { ADMIN_VIEWS } from "../../utils/constant";
import styles from "../../styles/scoped/admin/NewCashOutForm.module.css";
import { logger } from '../../utils/logger';
import { formatAmount } from '../../utils/utilities';


interface NewCashOutFormProps {
    selectedMenu: (view: number) => void;
}

export default function NewCashOutForm({ selectedMenu }: NewCashOutFormProps) {
    // Contexts and hooks
    const { selectedProjectParticipant } = useContext(AdminUserContext);
    const { selectedProject, refreshProjects } = useContext(AdminProjectContext);
    const { setNeedToRefreshData, previousMenu } = useContext(CommonContext);
    const { getWithAxios, postWithAxios } = useAxios();

    // Form state
    const [formData, setFormData] = useState<CashOutFormData | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasPenalty, setHasPenalty] = useState(false);
    const [penaltyInput, setPenaltyInput] = useState("");
    const [comment, setComment] = useState("");
    const [isLoadingData, setIsLoadingData] = useState(true);

    // Valeur numérique de la pénalité pour les calculs
    const penalty = parseFloat(penaltyInput) || 0;

    // Montant final calculé localement (montant net - pénalité)
    const finalAmount = formData ? Math.max(0, formData.withdrawalAmounts.netAmount - (hasPenalty ? penalty : 0)) : 0;

    // UI state
    const [globalError, setGlobalError] = useState('');
    const [globalInfo, setGlobalInfo] = useState('');
    const [formErrors, setFormErrors] = useState({
        penalty: '',
        comment: '',
    });
    // Load form data from API only on component mount
    useEffect(() => {
        const loadFormData = async () => {
            if (!selectedProject || !selectedProjectParticipant) {
                setGlobalError("Données manquantes pour charger le formulaire");
                setIsLoadingData(false);
                return;
            }

            setIsLoadingData(true);
            setGlobalError('');

            try {
                const url = `/admin/projects/${selectedProject.uuid}/users/${selectedProjectParticipant.uuid}/cash-out-form-data`;
                const data = await getWithAxios(url);
                setFormData(data);
            } catch (error: any) {
                logger.error('Error loading cash-out form data:', error);

                // Gestion spécifique de l'erreur 400
                if (error.response?.status === 400 && error.response?.data?.error?.message) {
                    setGlobalError(error.response.data.error.message);
                } else if (error.response?.data?.message) {
                    setGlobalError(error.response.data.message);
                } else if (error.message) {
                    setGlobalError(error.message);
                } else {
                    setGlobalError('Erreur lors du chargement des données');
                }
            } finally {
                setIsLoadingData(false);
            }
        };

        loadFormData();
    }, [selectedProject, selectedProjectParticipant]);

    // Validation du formulaire
    const validateForm = () => {
        const errors = {
            penalty: validatePenalty(),
            comment: '',
        };

        setFormErrors(errors);
        return !errors.penalty && !errors.comment;
    };

    const validatePenalty = () => {
        if (hasPenalty) {
            if (penalty < 0) {
                return 'La pénalité ne peut pas être négative';
            }
            if (formData && penalty > formData.withdrawalAmounts.netAmount) {
                return 'La pénalité ne peut pas dépasser le montant net';
            }
        }
        return '';
    };

    // Handle form submission
    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        if (!formData || !selectedProject || !selectedProjectParticipant) {
            setGlobalError("Données manquantes pour effectuer le retrait");
            return;
        }

        if (!formData.isValid) {
            setGlobalError("Veuillez corriger les erreurs du formulaire");
            return;
        }

        setIsSubmitting(true);
        setGlobalError("");

        try {
            const request: CashOutRequest = {
                projectUuid: selectedProject.uuid!,
                userUuid: selectedProjectParticipant.uuid,
                penalty: hasPenalty ? penalty : 0,
                date: new Date().toISOString(),
                withdrawalAmount: formData.currentWithdrawalAmount,
                comment,
            };

            const response = await postWithAxios('/admin/cash-out', request);
            if (response.success) {
                setGlobalInfo(response.message || 'Retrait effectué avec succès');
                setNeedToRefreshData(true);

                // Rafraîchir les participants pour mettre à jour le statut upToDate
                await refreshProjects();

                // Retourner au dashboard après un délai
                setTimeout(() => {
                    selectedMenu(ADMIN_VIEWS.DASHBOARD_VIEW);
                }, 1500);
            } else {
                setGlobalError(response.message || "Erreur lors du retrait");
            }
        } catch (error: any) {
            console.error('Cash-out error:', error);

            let message = "Un retrait a déjà été effectué pour ce mois";

            // Axios error
            if (error.response?.data?.message) {
                message = error.response.data.message;
            } else if (error.message) {
                message = error.message;
            }

            setGlobalError(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePenaltyChange = (checked: boolean) => {
        setHasPenalty(checked);
        if (!checked) {
            setPenaltyInput("");
        }
    };

    const handleBackClick = () => {
        selectedMenu(previousMenu || ADMIN_VIEWS.DASHBOARD_VIEW);
    };

    // Vérifier si les données requises sont disponibles
    if (!selectedProject || !selectedProjectParticipant) {
        return (
            <div className={styles.cashOutContainer}>
                <div className={styles.sectionHeader}>
                    <div className={styles.headerLeft}>
                        <div className={styles.sectionIcon}>
                            <MonetizationOn/>
                        </div>
                        <h1 className={styles.sectionTitle}>Nouveau Retrait</h1>
                    </div>
                    <button className={styles.backButton} onClick={handleBackClick}>
                        <ArrowBack/>
                        Retour
                    </button>
                </div>

                <Alert severity="error" className={styles.styledAlert}>
                    {!selectedProject && "Aucun projet sélectionné."}
                    {!selectedProjectParticipant && "Aucun participant sélectionné."}
                    {" "}Veuillez retourner au tableau de bord et sélectionner un participant.
                </Alert>

                <Alert severity="info" className={styles.styledAlert}>
                    Pour effectuer un retrait, cliquez sur le bouton "Retrait" d'un participant depuis le tableau de bord.
                </Alert>
            </div>
        );
    }

    if (isLoadingData) {
        return (
            <div className={styles.cashOutContainer}>
                <div className={styles.sectionHeader}>
                    <div className={styles.headerLeft}>
                        <div className={styles.sectionIcon}>
                            <MonetizationOn />
                        </div>
                        <h1 className={styles.sectionTitle}>Nouveau Retrait</h1>
                    </div>
                    <button className={styles.backButton} onClick={handleBackClick}>
                        <ArrowBack/>
                        Retour
                    </button>
                </div>

                <div className={styles.loadingContainer}>
                    <Alert severity="info" className={styles.styledAlert}>
                        Chargement des données de retrait...
                    </Alert>
                </div>
            </div>
        );
    }

    if (!formData) {
        return (
            <div className={styles.cashOutContainer}>
                <div className={styles.sectionHeader}>
                    <div className={styles.headerLeft}>
                        <div className={styles.sectionIcon}>
                            <MonetizationOn />
                        </div>
                        <h1 className={styles.sectionTitle}>Nouveau Retrait</h1>
                    </div>
                    <button className={styles.backButton} onClick={handleBackClick}>
                        <ArrowBack/>
                        Retour
                    </button>
                </div>

                {globalError && (
                    <Alert severity="error" className={styles.styledAlert}>
                        {globalError}
                    </Alert>
                )}
            </div>
        );
    }

    const isFormValid = formData.isValid && !formErrors.penalty && !formErrors.comment;

    return (
        <div className={styles.cashOutContainer}>
            <div className={styles.sectionHeader}>
                <div className={styles.headerLeft}>
                    <div className={styles.sectionIcon}>
                        <MonetizationOn />
                    </div>
                    <h1 className={styles.sectionTitle}>Nouveau Retrait</h1>
                </div>
                <button className={styles.backButton} onClick={handleBackClick}>
                    <ArrowBack/>
                    Retour
                </button>
            </div>

            {globalError && (
                <Alert severity="error" className={styles.styledAlert}>
                    {globalError}
                </Alert>
            )}

            <Snackbar
                open={globalInfo !== ''}
                autoHideDuration={6000}
                onClose={() => setGlobalInfo('')}
                message={globalInfo}
            />

            {/* Informations du participant */}
            <Card className={styles.infoCard}>
                <CardContent className={styles.infoCardContent}>
                    <div className={styles.participantInfo}>
                        <div className={styles.participantAvatar}>
                            {formData.user.firstname?.charAt(0)}
                            {formData.user.lastname?.charAt(0)}
                        </div>
                        <div className={styles.participantDetails}>
                            <h3 className={styles.participantName}>
                                {formData.user.firstname} {formData.user.lastname}
                            </h3>
                            <div className={styles.participantMeta}>
                                <Chip
                                    className={styles.metaChip}
                                    icon={<Person />}
                                    label={`Membre depuis ${new Date(selectedProjectParticipant.createdAt!)?.toLocaleDateString() || 'N/A'}`}
                                    variant="outlined"
                                />
                                <Chip
                                    className={styles.metaChip}
                                    icon={<AccountBalance />}
                                    label={`${selectedProjectParticipant.totalShare || 0} parts`}
                                    variant="outlined"
                                />
                            </div>
                        </div>
                    </div>

                    <div className={styles.amountSummary}>
                        <div className={`${styles.summaryItem} ${styles.summaryItemSuccess}`}>
                            <p className={styles.summaryLabel}>Montant brut</p>
                            <h4 className={styles.summaryValue}>{formData.withdrawalAmounts.formattedGrossAmount}</h4>
                        </div>
                        <div className={`${styles.summaryItem} ${styles.summaryItemWarning}`}>
                            <p className={styles.summaryLabel}>Réserve</p>
                            <h4 className={styles.summaryValue}>{formData.withdrawalAmounts.formattedCashFundAmount}</h4>
                        </div>
                        <div className={`${styles.summaryItem} ${styles.summaryItemPrimary}`}>
                            <p className={styles.summaryLabel}>Montant net</p>
                            <h4 className={styles.summaryValue}>{formData.withdrawalAmounts.formattedNetAmount}</h4>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {formData.withdrawalHistory.length > 0 && (
                <Card className={styles.historyCard}>
                    <CardContent className={styles.historyCardContent}>
                        <h3 className={styles.historyTitle}>
                            <History />
                            Historique des retraits
                        </h3>
                        <TableContainer component={Paper}>
                            <Table className={styles.styledTable}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Date</TableCell>
                                        <TableCell align="right">Montant</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {formData.withdrawalHistory.map((withdrawal) => (
                                        <TableRow key={withdrawal.uuid}>
                                            <TableCell>{withdrawal.formattedDate}</TableCell>
                                            <TableCell align="right">{withdrawal.formattedAmount}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                </Card>
            )}

            <Card className={styles.formCard}>
                <CardContent className={styles.formCardContent}>
                    <h3 className={styles.formTitle}>
                        <MonetizationOn />
                        Détails du retrait
                    </h3>

                    <div className={styles.penaltySection}>
                        <FormControlLabel
                            className={styles.styledFormControlLabel}
                            control={
                                <Checkbox
                                    checked={hasPenalty}
                                    onChange={(e) => handlePenaltyChange(e.target.checked)}
                                />
                            }
                            label="Appliquer une pénalité"
                        />

                        {hasPenalty && (
                            <>
                                <InputLabel className={styles.styledInputLabel}>Montant de la pénalité (€)</InputLabel>
                                <TextField
                                    className={styles.styledTextField}
                                    fullWidth
                                    type="text"
                                    value={penaltyInput}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        // Permettre uniquement les chiffres, le point décimal et la virgule
                                        if (value === "" || /^[0-9]*[.,]?[0-9]*$/.test(value)) {
                                            setPenaltyInput(value.replace(",", "."));
                                        }
                                    }}
                                    error={!!formErrors.penalty}
                                    placeholder="0.00"
                                    InputProps={{
                                        startAdornment: <Warning />,
                                    }}
                                    helperText={`Maximum: ${formatAmount(formData.withdrawalAmounts.netAmount)}`}
                                />
                                {formErrors.penalty && (
                                    <Alert severity="error" className={styles.styledAlert}>
                                        {formErrors.penalty}
                                    </Alert>
                                )}

                                <div className={styles.amountSummary} style={{ marginTop: '1rem' }}>
                                    <div className={`${styles.summaryItem} ${styles.summaryItemPrimary}`}>
                                        <p className={styles.summaryLabel}>Montant final à verser</p>
                                        <h4 className={styles.summaryValue}>{formatAmount(finalAmount)}</h4>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <InputLabel className={styles.styledInputLabel}>Commentaire (optionnel)</InputLabel>
                    <TextField
                        className={styles.commentField}
                        fullWidth
                        multiline
                        rows={3}
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        placeholder="Raison du retrait, commentaires..."
                    />

                    {formData.validationErrors.amount && (
                        <Alert severity="error" className={styles.styledAlert}>
                            {formData.validationErrors.amount}
                        </Alert>
                    )}

                    {!formData.isValid && (
                        <Alert severity="warning" className={styles.styledAlert}>
                            <Warning /> Impossible d'effectuer le retrait. Vérifiez les conditions requises.
                        </Alert>
                    )}

                    <Button
                        className={styles.submitButton}
                        fullWidth
                        onClick={handleSubmit}
                        disabled={!isFormValid || isSubmitting}
                        startIcon={<CheckCircle />}
                    >
                        {isSubmitting ? 'Traitement en cours...' : 'Confirmer le retrait'}
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
}
